import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGetTypeSubCatQuery } from "slices/common/offerSlice";
import { useGetProductSubCatQuery } from "slices/beautyApp/productSlice";
import { useGetCatBasedSubCatQuery } from "slices/eCommerce/shopProducts";
import { IMAGE_URL } from "network";
import { uploadImage } from "services/imageUpload";

const steps = ["Select Category", "Category Details"];

const AddOffer = ({ open, onClose, onSubmit, initialData = null }) => {
  const [step, setStep] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");

  const formik = useFormik({
    initialValues: {
      type: "",
      category: "",
      subCategory: "",
      title: "",
      image: "",
    },
    validationSchema: [
      Yup.object().shape({
        type: Yup.string().required("Main category is required"),
        category: Yup.string().required("category is required"),
        subCategory: Yup.string().required("category type is required"),
      }),
      Yup.object().shape({
        title: Yup.string().required("Title is required"),
        image: Yup.string().required("Image is required"),
      }),
    ][step],
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      onSubmit(values);
      handleReset();
    },
  });

  // Common subcategories (both Beauty & E-Commerce)
  const { data: subcategories } = useGetTypeSubCatQuery(
    { selectedCategory: formik.values.type },
    { skip: !formik.values.type }
  );

  // Conditionally fetch subCategorys based on selected type
  const { data: beautyTypes, isSuccess: beautySuccess } =
    useGetProductSubCatQuery(formik.values.category, {
      skip: formik.values.type !== "Beauty" || !formik.values.category,
    });

  const { data: ecommerceTypes, isSuccess: ecommerceSuccess } =
    useGetCatBasedSubCatQuery(formik.values.category, {
      skip: formik.values.type !== "E-Commerce" || !formik.values.category,
    });

  const subCategoryData =
    formik.values.type === "Beauty"
      ? beautyTypes?.data
      : formik.values.type === "E-Commerce"
      ? ecommerceTypes?.data
      : [];

  console.log("subCategoryData", subCategoryData, beautyTypes, ecommerceTypes);

  useEffect(() => {
    if (initialData) {
      formik.setValues({
        type:
          initialData.category_type === "Shop_Categories"
            ? "E-Commerce"
            : "Beauty" || "",
        category: initialData.category?._id || "",
        subCategory: initialData.subCategory?._id || "",
        title: initialData.title || "",
        image: initialData.image || "",
      });
      setPreviewUrl(IMAGE_URL + initialData.image);
      setStep(0);
    } else {
      formik.resetForm();
      setStep(0);
    }
  }, [initialData, open]);

  useEffect(() => {
    if (!initialData) {
      formik.setFieldValue("category", "");
      formik.setFieldValue("subCategory", "");
    }
  }, [formik.values.type]);

  useEffect(() => {
    if (!initialData) {
      formik.setFieldValue("subCategory", "");
    }
  }, [formik.values.category]);

  const handleNext = async (e) => {
    if (e?.preventDefault) e.preventDefault(); // Safeguard
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      setStep(step + 1);
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleReset = () => {
    if (!initialData) {
      formik.resetForm();
      setPreviewUrl("");
      setStep(0);
    }
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageName = await uploadImage(file);
      setPreviewUrl(IMAGE_URL + imageName);
      formik.setFieldValue("image", imageName);
    }
  };

  return (
    <Dialog open={open} onClose={handleReset} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? "Update Offer" : "Add Offer"}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={formik.handleSubmit}>
          {step === 0 && (
            <Box mt={3}>
              <TextField
                select
                name="type"
                fullWidth
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                margin="normal"
                SelectProps={{ native: true }}
                error={formik.touched.type && Boolean(formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
              >
                <option value="">-- Select Type --</option>
                <option value="Beauty">Beauty</option>
                <option value="E-Commerce">E-Commerce</option>
              </TextField>

              {subcategories?.data?.data?.length > 0 && (
                <TextField
                  select
                  name="category"
                  fullWidth
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="normal"
                  SelectProps={{ native: true }}
                  error={
                    formik.touched.category && Boolean(formik.errors.category)
                  }
                  helperText={formik.touched.category && formik.errors.category}
                >
                  <option value="">-- Select category --</option>
                  {subcategories.data.data.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {sub.name}
                    </option>
                  ))}
                </TextField>
              )}

              {subCategoryData?.length > 0 && (
                <TextField
                  select
                  name="subCategory"
                  fullWidth
                  value={formik.values.subCategory}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  margin="normal"
                  SelectProps={{ native: true }}
                  error={
                    formik.touched.subCategory &&
                    Boolean(formik.errors.subCategory)
                  }
                  helperText={
                    formik.touched.subCategory && formik.errors.subCategory
                  }
                >
                  <option value="">-- Select Type --</option>
                  {subCategoryData?.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {sub.name}
                    </option>
                  ))}
                </TextField>
              )}
            </Box>
          )}

          {step === 1 && (
            <Box mt={3}>
              <TextField
                label="Title"
                name="title"
                fullWidth
                margin="normal"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />

              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ marginTop: "16px" }}
                required={!initialData?.image}
              />
              {previewUrl && (
                <img
                  crossorigin="anonymous"
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    marginTop: 16,
                    width: "100px",
                    maxHeight: 200,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              type="button"
              disabled={step === 0}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
            {step === steps.length - 1 ? (
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            ) : (
              <Button type="button" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOffer;
